import { useEffect, useMemo, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlbumArt } from '../../shared/components/AlbumArt';
import { CloseIcon } from '../../shared/components/icons';
import { catalogPreview } from './useSearch';

const EASE = [0.16, 1, 0.3, 1];

// Bienvenida en modo playlist: avisa que solo se pide de la selección del local y muestra parte de ella.
export function PlaylistIntro({ tracks, onClose, onChoose }) {
  const preview = useMemo(() => catalogPreview(tracks, { songs: 5 }), [tracks]);
  const closeRef = useRef(null);
  const reduce = useReducedMotion();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Solo al montar: si dependiera de onClose, cada poll de la sala volvería a robar el foco.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: EASE }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-end justify-center bg-lounge-base/80 backdrop-blur-sm
                 sm:items-center sm:p-6"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="intro-title"
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90dvh] w-full max-w-[480px] overflow-y-auto rounded-t-lg border
                   border-lounge-line bg-lounge-card pb-[env(safe-area-inset-bottom)] shadow-sleeve sm:rounded-lg"
      >
        <div aria-hidden className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full
                                    bg-lounge-brass/[0.07] blur-[80px]" />

        <header className="relative px-5 pb-4 pt-6">
          <button
            ref={closeRef}
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full
                       text-lounge-stone transition-colors hover:text-lounge-text focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-lounge-brass"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lounge-brass">Selección del local</p>
          <h2 id="intro-title" className="mt-2 pr-8 font-serif text-[24px] font-medium leading-tight text-lounge-text">
            Esta noche suena lo que eligió la casa
          </h2>
          <p className="mt-2 font-inter text-sm text-lounge-stone">
            Puedes pedir cualquier canción de la selección especial del local
            {' · '}<span className="font-mono text-[12px] text-lounge-brass">{preview.total} canciones</span>
          </p>
        </header>

        <section aria-label="Artistas" className="relative pb-4">
          <p className="mb-2.5 px-5 font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone">Artistas</p>
          <ul className="flex snap-x scroll-px-5 gap-3 overflow-x-auto px-5 pb-1 [scrollbar-width:none]">
            {preview.artists.map((a) => (
              <li key={a.name} className="w-16 shrink-0 snap-start text-center">
                <AlbumArt image={a.image} className="w-16" />
                <p className="mt-1.5 truncate font-inter text-[11px] text-lounge-text">{a.name}</p>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="Algunas canciones" className="relative px-5 pb-2">
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone">Algunas canciones</p>
          <ul className="flex flex-col gap-1.5">
            {preview.songs.map((s) => (
              <li key={s.uri} className="flex items-center gap-3 rounded bg-lounge-raised/60 p-2">
                <AlbumArt image={s.image} className="w-10" />
                <div className="min-w-0">
                  <p className="truncate font-inter text-sm font-medium text-lounge-text">{s.name}</p>
                  <p className="truncate font-inter text-xs text-lounge-stone">{s.artist}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="relative p-5">
          <button
            type="button"
            onClick={onChoose}
            className="h-12 w-full rounded bg-lounge-brass font-inter font-semibold text-lounge-base transition
                       hover:bg-amber hover:shadow-brass focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-lounge-brass focus-visible:ring-offset-2 focus-visible:ring-offset-lounge-card
                       active:scale-[0.98]"
          >
            Elegir mi canción
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
